import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Table } from "antd";

import trancheTableColumns from "./json/trancheTableColumns.json";
import { useEditableCell } from "../../hooks/useEditableTableCell";
import {
  emdash,
  trancheOptionAstericksMessage,
} from "../../utils/Constants/systemSetting";
import useFormatTrancheSummaryResults from "../../hooks/useFormatTrancheSummaryResults";

export default function TrancheSummaryTable({
  calculatorPageSelected,
  allTrancheTableValues,
  optionsCalculatorValues,
  RSUCalculatorValues,
  generalInfoParams,
  includeTaxes = false,
}) {
  const renderEditableCell = useEditableCell();

  const [calculatedValuesUpdateNeeded, setCalculatedValuesUpdateNeeded] =
    useState(false);

  const formattedTrancheSummaryResults = useFormatTrancheSummaryResults(
    allTrancheTableValues,
    calculatorPageSelected,
    calculatorPageSelected === "RSU Schedule"
      ? RSUCalculatorValues
      : optionsCalculatorValues,
  );

  const [trancheTableValues, setTrancheTableValues] = useState(
    formattedTrancheSummaryResults,
  );

  // Populate tranche values based on the current page
  useEffect(() => {
    setTrancheTableValues(formattedTrancheSummaryResults);
  }, [
    trancheTableValues,
    formattedTrancheSummaryResults,
    setTrancheTableValues,
  ]);

  // Function for updating the tranche values
  const updateTrancheParams = useCallback(
    (ID, record, newValue) => {
      if (!trancheTableValues) return;

      const trancheIndex = trancheTableValues.findIndex(
        (item) => item.ID === ID,
      );

      const newTrancheValues = [...trancheTableValues];
      const updatedTrancheValues = {
        ...trancheTableValues[trancheIndex],
        [record.key]: newValue,
      };
      newTrancheValues[trancheIndex] = updatedTrancheValues;

      setTrancheTableValues(newTrancheValues);
      setCalculatedValuesUpdateNeeded(true);
    },
    [setTrancheTableValues, trancheTableValues],
  );

  const currentOptionCalculatorValues = useMemo(() => {
    return optionsCalculatorValues.find((item) => item.ID === "grant1");
  }, [optionsCalculatorValues]);

  // Trigger Updates for calculated values within the Tranche Table based on other tables
  useMemo(() => {
    if (currentOptionCalculatorValues || generalInfoParams[0]) {
      setCalculatedValuesUpdateNeeded(true);
    }
  }, [generalInfoParams, currentOptionCalculatorValues]);

  // Function For calculating Values in the Tranche Table
  useEffect(() => {
    if (!trancheTableValues?.length) return;

    if (calculatedValuesUpdateNeeded) {
      const newTrancheTableValues = [...trancheTableValues];

      setTrancheTableValues(newTrancheTableValues);
      setCalculatedValuesUpdateNeeded(false);
    }
  }, [
    setTrancheTableValues,
    trancheTableValues,
    calculatedValuesUpdateNeeded,
    currentOptionCalculatorValues,
  ]);

  // Define each of the Tranche columns based on trancheTableColumns.json
  const defaultColumns = useMemo(
    () => [
      // {
      //   title: "Name",
      //   dataIndex: "ID",
      //   align: "center",
      //   key: "ID",
      //   render: (item) => (item ? item.replace(" ", "\u00A0") : item),
      // },
      ...Object.values(trancheTableColumns)
        .filter((col) => !col.hideFromUser && !col.hideInSummaryView)
        .filter(
          (col) =>
            !includeTaxes || !(col?.isPreTaxValue && !col?.isAfterTaxValue),
        )
        .filter(
          (col) =>
            includeTaxes || !(!col?.isPreTaxValue && col?.isAfterTaxValue),
        )
        .filter(
          (col) =>
            calculatorPageSelected === "Options Schedule" ||
            !col.optionsCalculatorOnly,
        )
        .filter(
          (col) =>
            calculatorPageSelected === "RSU Schedule" || !col.RSUcalculatorOnly,
        )
        .map((col) => ({
          title: col.title,
          key: col.key,
          align: "center",
          dataType: col.dataType,
          defaultValue: col.defaultValue,
          hideFromUser: col.hideFromUser,
          isCalculatedValue:
            col.isCalculatedValue || col.isCalculatedValueInSummaryView,
          isTrancheColumn: true,
          isPreTaxValue: col.isPreTaxValue,
          isAfterTaxValue: col.isAfterTaxValue,
          hasHoverText: false, // No hoverText in combined table
        })),
    ],
    [calculatorPageSelected, includeTaxes],
  );

  // Function to add a render function to each column (to keep each ref in memory current)
  const [currentEditedCell, setCurrentEditedCell] = useState("");

  const trancheTableColumnsRendered = useMemo(
    () =>
      [...defaultColumns].map((col) => {
        if (col.isTrancheColumn) {
          col = {
            ...col,
            render: (text, record) => {
              const currentTrancheValues = trancheTableValues?.find(
                (item) => item.ID === record.ID,
              );

              const cellKey = `${col.key}-${record.ID}`;
              const isCurrentEditedCell = currentEditedCell === cellKey;

              const currentAssignedValue =
                typeof currentTrancheValues?.[col.key] === "object" &&
                col.key !== "trancheDate"
                  ? currentTrancheValues?.[col.key]?.value
                  : currentTrancheValues?.[col.key];

              const value =
                currentAssignedValue ||
                (isCurrentEditedCell
                  ? currentAssignedValue
                  : (col.key === "killAmountAfterTax" ||
                        col.key === "killAmount" ||
                        col.key === "killPercentage" ||
                        col.key === "intrinsicValue" ||
                        col.key === "intrinsicValueAfterTax" ||
                        col?.isAfterTaxValue) &&
                      record?.remainingFullValue
                    ? col.key === "killAmountAfterTax" ||
                      col.key === "killPercentage" ||
                      col.key === "killAmount"
                      ? "*"
                      : emdash
                    : col.key === "dividendsForegone"
                      ? 0
                      : "");

              return renderEditableCell(
                value,
                col,
                record.ID,
                col.dataType,
                updateTrancheParams,
                setCurrentEditedCell,
                { width: "100%" },
              );
            },
          };
        }
        return col;
      }),
    [
      defaultColumns,
      updateTrancheParams,
      renderEditableCell,
      trancheTableValues,
      currentEditedCell,
    ],
  );

  const astericksExists = useMemo(() => {
    return (
      calculatorPageSelected === "Options Schedule" &&
      trancheTableValues.some((item) => item?.initialUnits && !item?.killAmount)
    );
  }, [calculatorPageSelected, trancheTableValues]);

  // Finally, return the table with editable and calculate-able valuespt
  return (
    <>
      <Table
        columns={trancheTableColumnsRendered}
        dataSource={trancheTableValues}
        pagination={false}
        size="small"
      />
      {astericksExists && (
        <div style={{ marginTop: 10, marginLeft: 10 }}>
          {trancheOptionAstericksMessage}
        </div>
      )}
    </>
  );
}
